import { useCallback } from 'react';

import { useCommunityAccess } from '../../_hooks/useCommunityAccess';
import { useCommunityMembership } from '../../_hooks/useCommunityMembership';
import { useCommunityUpgradeReturn } from '../../_hooks/useCommunityUpgradeReturn';
import { useUpgradeHandoff } from '../../_hooks/useUpgradeHandoff';

export interface UpgradeJoinHandoff {
  destination: string | null;
  isAwaitingAccess: boolean;
  markAwaitingAccess: () => void;
  onAccessGranted: () => void;
}

interface UpgradeJoinPurchase {
  destination?: string;
}

// Reopening `community_join` after the revalidation lands on the join form, because the
// layout routes that intent by `joinGate`.
export const useUpgradeJoinHandoff = (destination: string | null): UpgradeJoinHandoff => {
  const { isCommunityAccessible } = useCommunityAccess();
  const { openJoinModal } = useCommunityMembership();
  const { resumesInviteAcceptance } = useCommunityUpgradeReturn(destination);

  const isGranted = useCallback(() => isCommunityAccessible, [isCommunityAccessible]);
  const onGranted = useCallback(
    ({ destination }: UpgradeJoinPurchase) => {
      if (!resumesInviteAcceptance) openJoinModal(undefined, destination);
    },
    [openJoinModal, resumesInviteAcceptance],
  );

  const { awaiting, markAwaitingAccess, onAccessGranted } = useUpgradeHandoff<UpgradeJoinPurchase>({
    modalType: 'community_join',
    isGranted,
    onGranted,
  });

  return {
    destination,
    isAwaitingAccess: awaiting !== null,
    markAwaitingAccess: useCallback(
      () => markAwaitingAccess({ destination: destination ?? undefined }),
      [markAwaitingAccess, destination],
    ),
    onAccessGranted,
  };
};
